import { ChangeDetectionStrategy, Component, computed, DestroyRef, effect, input, output, signal, untracked } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { getTailwindColorValue, ThemeColorName } from '@examdojo/styles';
import { tap } from 'rxjs/operators';
import { SafeHtml } from '@angular/platform-browser';
import type { WidgetSliderVariant } from '../../model/widget-control.model';

@Component({
  selector: 'dojo-slider',
  standalone: true,
  imports: [ReactiveFormsModule],
  templateUrl: './slider.component.html',
  styleUrl: './slider.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SliderComponent {
  constructor(private readonly destroyRef: DestroyRef) {
    this.initInputControlEffect();
  }

  readonly param = input.required<string>();
  /** Optional pre-rendered HTML (e.g. KaTeX) to display instead of the plain param text */
  readonly paramHtml = input<SafeHtml>();
  readonly min = input<number>(0);
  readonly max = input<number>(100);
  readonly step = input<number>(1);
  readonly value = input<number>(0);
  readonly editable = input<boolean>(false);
  /** Theme color name for the slider handle and trace (e.g., 'teal', 'pink', 'sky') */
  readonly color = input<ThemeColorName>('sky');
  /** Visual variant: 'default' or 'minimal' (compact track, smaller handle, tighter spacing) */
  readonly variant = input<WidgetSliderVariant>('default');
  /** Whether to show the param label (e.g. "teal =") next to the value */
  readonly showLabel = input<boolean>(true);

  readonly valueChange = output<number>();

  readonly inputCtrl = new FormControl<number>(0, { nonNullable: true });
  readonly currentValue = signal<number>(0);

  /** Calculate progress percentage for the colored trace */
  readonly progressPercent = computed(() => {
    const minVal = this.min();
    const maxVal = this.max();
    const val = this.currentValue();
    if (maxVal === minVal) {
      return '0%';
    }
    const percent = ((val - minVal) / (maxVal - minVal)) * 100;
    return `${Math.max(0, Math.min(100, percent))}%`;
  });

  /** Resolve color input to CSS variable */
  readonly resolvedColor = computed(() => getTailwindColorValue(this.color()));

  private lastEmittedValue: number | null = null;

  onSliderDrag(event: Event): void {
    const target = event.target as HTMLInputElement;
    const val = parseFloat(target.value);
    this.currentValue.set(val);
    this.inputCtrl.setValue(val, { emitEvent: false });
    if (val !== this.lastEmittedValue) {
      this.lastEmittedValue = val;
      this.valueChange.emit(val);
    }
  }

  /** Emit value only when drag is complete */
  onSliderChange(event: Event): void {
    const target = event.target as HTMLInputElement;
    const val = parseFloat(target.value);
    if (val !== this.lastEmittedValue) {
      this.currentValue.set(val);
      this.inputCtrl.setValue(val, { emitEvent: false });
      this.lastEmittedValue = val;
      this.valueChange.emit(val);
    }
  }

  private initInputControlEffect(): void {
    // Sync input value to internal state
    effect(
      () => {
        const val = this.value();
        this.currentValue.set(val);
        this.inputCtrl.setValue(val, { emitEvent: false });
        // Always sync lastEmittedValue when external value changes
        // This prevents stale comparisons when user adjusts back to a previous value
        this.lastEmittedValue = val;
      },
      { allowSignalWrites: true },
    );

    // Update validators and auto-clamp when min/max change
    effect(
      () => {
        const minVal = this.min();
        const maxVal = this.max();
        this.inputCtrl.setValidators([Validators.required, Validators.min(minVal), Validators.max(maxVal)]);

        const currentVal = untracked(() => this.currentValue());
        const clamped = Math.max(minVal, Math.min(maxVal, currentVal));
        if (clamped !== currentVal) {
          this.currentValue.set(clamped);
          this.inputCtrl.setValue(clamped);
        } else {
          this.inputCtrl.updateValueAndValidity();
        }
      },
      { allowSignalWrites: true },
    );

    // Handle input control changes - only emit when value actually changes
    this.inputCtrl.valueChanges
      .pipe(
        tap((val) => {
          if (this.inputCtrl.valid && val !== null && val !== this.lastEmittedValue) {
            this.currentValue.set(val);
            this.lastEmittedValue = val;
            this.valueChange.emit(val);
          }
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }
}
